import styled from "styled-components";
import {fontFamilies, radii} from "theme/tokens";
import {eyebrowTextCss} from "theme/typography";
import {goldClaimFill, goldOfferWash} from "upsell/offerSurface.styles";

/**
 * The day-14 rung's offer, inline above the plan table.
 *
 * A PANEL rather than a dialog, deliberately. The pricing route already hosts one dialog of its own
 * (the checkout redirect) and the nav's pricing sheet hosts the exit offer's; a third modal on the same
 * page would put two focus traps on screen the moment a reader opened either. A panel also stays
 * readable while the reader compares it against the plan cards below, which is the comparison the
 * offer is asking them to make.
 */
export const Panel = styled.section`
	max-width: 680px;
	margin: 18px auto 4px;
	padding: 18px 20px 16px;
	box-sizing: border-box;
	text-align: left;
	border-radius: ${radii.card};
	/* The same wash every full gold offer surface sits on, imported rather than restated: the wash
	   needs its line in both modes and that reasoning belongs in one place. */
	${goldOfferWash};

	@media (max-width: 480px) {
		margin: 14px auto 2px;
		padding: 14px 14px 12px;
	}
`;

export const Eyebrow = styled.div`
	${eyebrowTextCss};
	color: ${({theme}) => theme.tokens.ink2};
	font-weight: 700;
	letter-spacing: 0.1em;
`;

export const Title = styled.p`
	margin: 6px 0 0;
	font-size: 20px;
	font-weight: 700;
	line-height: 1.25;
	color: ${({theme}) => theme.tokens.ink};
`;

/** The renewal terms, stated above the CTA rather than as fine print. */
export const AfterNote = styled.p`
	margin: 10px 0 0;
	font-size: 13.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const Unavailable = styled.p`
	margin: 8px 0 0;
	font-size: 14px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The CTA, taking the same fill as the exit offer's own Continue: the contrast reasoning behind gold
 * on `goldInk` with its edge lives in {@link ../../upsell/offerSurface.styles.goldClaimFill} and is
 * not worth a second copy.
 *
 * Its own component rather than the dialog's, because this rung is a panel and not a dialog — so the
 * 16px above it is restated here rather than inherited (kitecorp/transferify-ui#2131). It is the same
 * gap the two offer dialogs give the same button above the same consent control, and the reason is
 * theirs: {@link ../../upsell/ExitOfferDialog.styles.Continue}.
 */
export const Continue = styled.button`
	${goldClaimFill};
	margin: 16px 0 0;
	width: 100%;
	min-height: 44px;
	border-radius: ${radii.control};
	border: none;
	cursor: pointer;
	font-family: ${fontFamilies.sans};
	font-size: 15px;
	font-weight: 600;

	&:disabled {
		cursor: default;
		opacity: 0.6;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;
